import { action, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import ICensusUploadPageStore from 'store/modules/census/iCensusUploadPageStore';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import { redirectToRoute } from 'routes/redirectToRoute';
import { CensusGroupModule } from 'store/modules/census/group-based/censusGroupModule';
import QuoteFlowDelegate from 'store/flows/quote/quoteFlowDelegate';

interface DomainProps {
  groupId: string;
  enrollmentPeriodId: string;
  cartId: string;
}
export default class IMQCensusUploadPageStore extends ICensusUploadPageStore<DomainProps, CensusGroupModule> {
  protected _censusModule: CensusGroupModule;
  protected _domainData: DomainProps;
  public readonly flowDelegate: QuoteFlowDelegate;

  public async getCensusData(): Promise<void> {
    this.setIsLoading(true);
    await Promise.all([this._censusModule.getPreviouslyUploadedFiles(), this._censusModule.uploadExistingCensus()]);
    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  public onCompleteRedirect(): void {
    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES_CENSUS_REVIEW, {
      groupId: this._domainData.groupId,
      periodId: this._domainData.enrollmentPeriodId,
      cartId: this._domainData.cartId,
    });
  }

  constructor(domainData: DomainProps) {
    super();
    this._censusModule = new CensusGroupModule({
      groupId: domainData.groupId,
      enrollmentPeriodId: domainData.enrollmentPeriodId,
    });
    this._domainData = domainData;
    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: {
        groupId: domainData.groupId,
        periodId: domainData.enrollmentPeriodId,
        cartId: domainData.cartId,
      },
      nextPageId: QuoteRouteV2Id.IMQ_ADD_EMPLOYEES_CENSUS_REVIEW,
    });

    makeObservable<IMQCensusUploadPageStore, '_censusModule' | '_domainData'>(this, {
      _censusModule: observable,
      _domainData: observable,

      getCensusData: action.bound,
      onCompleteRedirect: action.bound,
    });
  }
}

const IMQCensusUploadPageStoreContext = createContext(new IMQCensusUploadPageStore({} as DomainProps));

export const useIMQCensusUploadPageStore = (): IMQCensusUploadPageStore => {
  return useContext(IMQCensusUploadPageStoreContext);
};

export { IMQCensusUploadPageStoreContext };
